import React, { useContext, useState } from 'react';
import SidePanelOverlay from '../side-panel-overlay/SidePanelOverlay';
import { cn } from '@/lib/utils';
import type { Portfolio } from 'venn-api';
import {
  CheckboxTree,
  flattenTree,
  Icon,
  type SelectableRow,
  StudioButton,
  type TreeNode,
  type TreeViewRow,
} from 'venn-ui-kit';
import { Checkbox } from '@/components/ui/checkbox';
import { MAX_SUBJECT_GROUP_SIZE, plural, useModal } from 'venn-utils';
import { ErrorMessage } from '../modals/data-uploader/components/page-parts';
import { PortfolioFilteringOnboardingVideoModal } from './PortfolioFilteringOnboardingVideoModal';
import { ThemeContext } from 'styled-components';

type FilterPortfolioProps = {
  portfolio: Portfolio;
  filteredItems: string[];
  otherPortfolioSubjectsQuantity: number;
  handleClose: () => void;
  handleApply: (selectedKeys: string[]) => void;
};
const toTreeNode = <T,>(
  node: T,
  getKey: (item: T) => string,
  getChildren: (item: T) => T[],
  collapsedKeys: string[],
): TreeNode<T> => ({
  data: node,
  key: getKey(node),
  children: collapsedKeys.includes(getKey(node))
    ? []
    : ((getChildren(node) ?? []).map((c) => toTreeNode(c, getKey, getChildren, collapsedKeys)) ?? []),
  selectedOverride: undefined,
});

export const FilterPortfolioPanel = ({
  portfolio,
  filteredItems,
  otherPortfolioSubjectsQuantity,
  handleClose,
  handleApply,
}: FilterPortfolioProps) => {
  const width =
    Math.max(
      55,
      portfolio.name.length,
      portfolio.children
        .map((c) => c.name)
        .map((c) => c.length)
        .reduce((a, b) => (a > b ? a : b), 0),
    ) *
      10 +
    50;

  const [selectedKeys, _setSelectedKeys] = useState<string[]>(filteredItems);
  const [collapsedKeys, setCollapsedKeys] = useState<string[]>([]);
  const [isAnyCheckboxClicked, setIsAnyCheckboxClicked] = useState<boolean>(false);
  const [isOnboardingVideoModalOpen, openOnboardingVideoModal, closeOnboardingVideoModal] = useModal();
  const { Videos } = useContext(ThemeContext);

  const portfolioTree = toTreeNode(
    portfolio,
    (p) => p.id.toString(),
    (p) => p.children,
    collapsedKeys,
  );
  const flattenedTree = flattenTree(portfolioTree);
  const getSubjectIdByPortfolio = (portfolio: Portfolio) =>
    portfolio.fund ? portfolio.fund.id : portfolio.id.toString();
  const getSubjectByKeys = (keys: string[]) =>
    new Set<string>(
      flattenedTree
        .filter((f) => keys.includes(f.key))
        .map((f) => f.data)
        .map((d) => getSubjectIdByPortfolio(d)),
    );
  const selectedSubjects = getSubjectByKeys(selectedKeys);
  const isSubjectLimitReached = selectedSubjects.size + otherPortfolioSubjectsQuantity >= MAX_SUBJECT_GROUP_SIZE;

  const setSelectedKeys = (keys: string[]) => {
    const removedKeys = selectedKeys.filter((k) => !keys.includes(k));
    const removedSubjectIds = getSubjectByKeys(removedKeys);
    const subjectIds = getSubjectByKeys(keys);
    const newSubjectIds = subjectIds.difference(removedSubjectIds);
    const newSelectedKeys = flattenedTree
      .filter((f) => newSubjectIds.has(getSubjectIdByPortfolio(f.data)))
      .map((r) => r.key);
    _setSelectedKeys(newSelectedKeys);
  };

  const onCollapseChanged = (key: string, collapsedKeys: string[]) => {
    const newCollapsedKeys = collapsedKeys.includes(key)
      ? collapsedKeys.filter((k) => k !== key)
      : [...collapsedKeys, key];
    setCollapsedKeys(newCollapsedKeys);
  };

  const rowRenderer = (
    row: TreeViewRow<Portfolio> & SelectableRow,
    collapsedKeys: string[],
    onCollapseChanged: (key: string, collapsedKeys: string[]) => void,
  ): React.ReactNode => {
    const isCollapsed = collapsedKeys.includes(row.key);
    return (
      <>
        <div className={cn(`pl-[24px] h-[33px] items-center flex ${row.selected ? ' bg-venn-grey-10' : ''} `)}>
          <div className="flex-shrink-0">
            <Checkbox
              key={row.key}
              state={row.selected ? 'checked' : 'unchecked'}
              disabled={isSubjectLimitReached && isAnyCheckboxClicked && !row.selected}
              onCheckedChange={() => {
                setIsAnyCheckboxClicked(true);
                if (!isSubjectLimitReached || row.selected) {
                  row.onSelect(row.key);
                }
              }}
            />
            <span style={{ marginLeft: `${row.level}rem` }} />
            {!row.isLeaf && (
              <button
                type="button"
                onClick={() => onCollapseChanged(row.key, collapsedKeys)}
                aria-expanded={!isCollapsed}
                className="border-none bg-transparent cursor-pointer text-venn-grey-80 mr-[3px] ml-[6px]"
              >
                <i className="fa fa-caret-down" />
              </button>
            )}
            {isCollapsed && (
              <button
                type="button"
                onClick={() => onCollapseChanged(row.key, collapsedKeys)}
                aria-expanded={!isCollapsed}
                className="border-none bg-transparent cursor-pointer text-venn-grey-80 mr-[3px] ml-[6px]"
              >
                <i className="fa ml-[1.5px] fa-caret-right" />
              </button>
            )}
            {row.isLeaf && !isCollapsed && <span className="ml-[18px]" />}
          </div>
          <span className={cn(`text-[14px] truncate ${row.isLeaf && !isCollapsed ? 'font-normal' : 'font-bold'}`)}>
            {row.data?.name}
          </span>
        </div>
        <div className={cn('bg-venn-grey-30 h-px')} />
      </>
    );
  };

  return (
    <SidePanelOverlay.Root
      className={cn('!min-w-[30vw] !max-w-[80vw] !p-[1.5px]')}
      width={width}
      margin={0}
      side="right"
      isOpen
      handleClose={handleClose}
      useFocusTrap={false}
      hideTopBackButton
      scrollAlignRight
      closeOnEsc
      noPadding
    >
      <SidePanelOverlay.Header.Container paddingTop="default">
        <SidePanelOverlay.Header.Main title="Filter Portfolio" />
      </SidePanelOverlay.Header.Container>
      {isSubjectLimitReached && isAnyCheckboxClicked && (
        <ErrorMessage>
          Subject groups have a limit of {MAX_SUBJECT_GROUP_SIZE} subjects. Remove some subjects or move this portfolio
          to a new group in order to filter.
        </ErrorMessage>
      )}
      <SidePanelOverlay.Body className="pt-0 pl-0 pr-0 h-full overflow-y-auto">
        <div className="px-[24px] py-[8px] text-[14px] bg-venn-grey-10 border-0 border-b border-solid border-b-venn-grey-30 flex justify-between items-center">
          Filtered items act as individual subjects for this view.
          {Videos.portfolioFilteringOnboarding && (
            <StudioButton
              size="small"
              className="gap-[4px] text-venn-dark-blue"
              onClick={() => openOnboardingVideoModal()}
            >
              <Icon type="play" />
              <span className="text-[12px] leading-[12px]">Watch how this works</span>
            </StudioButton>
          )}
        </div>
        <CheckboxTree
          selectedKeys={selectedKeys}
          setSelectedKeys={setSelectedKeys}
          rows={flattenedTree as never}
          rowRenderer={(row: TreeViewRow<Portfolio> & SelectableRow) =>
            rowRenderer(row, collapsedKeys, onCollapseChanged)
          }
        />
        {isOnboardingVideoModalOpen && <PortfolioFilteringOnboardingVideoModal onClose={closeOnboardingVideoModal} />}
      </SidePanelOverlay.Body>
      <SidePanelOverlay.Footer
        cancelLabel="CANCEL"
        primaryLabel="Apply selection"
        primaryDisabled={selectedKeys === filteredItems}
        rightChildren={
          <span className="font-normal text-[12px]">
            {plural(selectedSubjects.size, {
              1: `${selectedSubjects.size} subject`,
              other: `${selectedSubjects.size} subjects`,
            })}
            &nbsp; selected
          </span>
        }
        secondaryLabel="Deselect all"
        secondaryDisabled={selectedKeys.length === 0}
        onCancel={handleClose}
        onPrimaryClick={() => handleApply(selectedKeys)}
        onSecondaryClick={() => setSelectedKeys([])}
      />
    </SidePanelOverlay.Root>
  );
};
